import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';

interface AdminRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'superadmin';
}

/**
 * AdminRoute - Optimized secure route guard
 * 
 * Security Architecture:
 * 1. Role is fetched from database via useUserRole hook (already server-validated)
 * 2. RLS policies at database level enforce data access control
 * 3. No redundant server calls - instant validation
 * 
 * This maintains security while providing instant page loads.
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children, requiredRole = 'admin' }) => {
  const { user, loading: authLoading } = useAuth();
  const { role, loading: roleLoading } = useUserRole();

  console.log('AdminRoute check:', { 
    userEmail: user?.email,
    userId: user?.id,
    role, 
    requiredRole, 
    authLoading, 
    roleLoading 
  });

  // Show loading state while auth/role loads
  if (authLoading || roleLoading) {
    console.log('AdminRoute: Still loading auth or role...');
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!user) {
    console.log('AdminRoute: No user, redirecting to /admin/auth');
    return <Navigate to="/admin/auth" replace />;
  }

  // Check permission (role is already server-validated via useUserRole)
  // IMPORTANT: Only check permission if role has been loaded (not null)
  // If role is null, it means it hasn't been fetched yet, so keep showing loading
  if (role === null) {
    console.log('AdminRoute: Role is null, continuing to show loading state');
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying permissions...</p>
        </div>
      </div>
    );
  }

  const hasPermission = requiredRole === 'superadmin'
    ? role === 'superadmin'
    : (role === 'admin' || role === 'superadmin');

  console.log('AdminRoute: Permission check result:', { hasPermission, role, requiredRole });

  // Show access denied ONLY if we're sure the role has been loaded and permission is denied
  if (!hasPermission) {
    console.error('AdminRoute: Access denied!', { role, requiredRole, user: user.email });
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-4">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            Required role: {requiredRole} | Your role: {role || 'none'}
          </p>
          <p className="text-xs text-muted-foreground bg-muted p-4 rounded">
            Debug: User: {user?.email} ({user?.id})
          </p>
        </div>
      </div>
    );
  }

  console.log('AdminRoute: Access granted, rendering protected content');
  // Render protected content
  return <>{children}</>;
};

export default AdminRoute;